import { getCachedSession } from '../hooks/useAuth';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Crown, Sparkles, Lock, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

const UPGRADE_MESSAGES = {
  board_limit: {
    title: "Board Limit Reached",
    description: "You've reached the maximum number of boards on your current plan.",
    benefit: "Upgrade to Pro for unlimited boards and organize all your projects.",
    icon: Lock
  },
  search_limit: {
    title: "Search Limit Reached",
    description: "You've used all your visual searches for this month.",
    benefit: "Upgrade to Pro for unlimited visual searches and discover materials faster.",
    icon: Lock
  },
  feature_locked: {
    title: "Premium Feature",
    description: "This feature is not available on your current plan.",
    benefit: "Upgrade to unlock all professional features and grow your business.",
    icon: Lock
  },
  pdf_export: {
    title: "PDF Export",
    description: "Export your boards to professional PDF documents.",
    benefit: "Upgrade to Pro to export beautiful PDF presentations for your clients.",
    icon: Lock
  },
  team_invite: {
    title: "Team Collaboration",
    description: "Invite team members to collaborate on projects.",
    benefit: "Upgrade to Studio plan for multi-user team accounts and shared workspaces.",
    icon: Lock
  },
  material_limit: {
    title: "Material Limit Reached",
    description: "You've reached the maximum materials per board on your plan.",
    benefit: "Upgrade to Pro for unlimited materials per board.",
    icon: Lock
  }
};

export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  reason = 'feature_locked',
  customMessage = null
}) => {
  const messageConfig = UPGRADE_MESSAGES[reason] || UPGRADE_MESSAGES.feature_locked;
  const IconComponent = messageConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">
            {customMessage?.title || messageConfig.title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {customMessage?.description || messageConfig.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-primary/5 border border-primary/10 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-stone-700">
                {customMessage?.benefit || messageConfig.benefit}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Link to="/pricing" onClick={onClose}>
              <Button className="w-full bg-primary hover:bg-primary/90" data-testid="upgrade-modal-cta">
                View Plans <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full" 
              onClick={onClose}
            >
              Maybe Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Hook for checking limits and showing upgrade modal
export const useSubscriptionLimits = () => {
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [upgradeReason, setUpgradeReason] = React.useState('feature_locked');

  const checkLimit = async (action) => {
    try {
      // Use cached auth for token
      const session = getCachedSession();
      if (!session?.access_token) return { allowed: true };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/check-limit/${action}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      
      const data = await res.json();
      
      if (!data.allowed) {
        setUpgradeReason(data.reason || 'feature_locked');
        setShowUpgrade(true);
        return { allowed: false, ...data };
      }
      
      return { allowed: true, ...data };
    } catch (err) {
      console.error('Failed to check limit:', err);
      return { allowed: true };
    }
  };

  return {
    showUpgrade,
    setShowUpgrade,
    upgradeReason,
    checkLimit,
    UpgradeModal: () => (
      <UpgradeModal 
        isOpen={showUpgrade} 
        onClose={() => setShowUpgrade(false)} 
        reason={upgradeReason}
      />
    )
  };
};

export default UpgradeModal;
